/**
 * The realtime contract the portal codes against (design 4.1). Pure types and helpers: the only file
 * that imports the `centrifuge` SDK is `realtime/centrifugeTransport.ts`, so tests run on
 * `test/fakeRealtime.ts` and nothing here opens a socket.
 *
 * A publication is a HINT ("something changed on this load / carrier"): the screen re-reads its own
 * query, it never patches state from the payload (a first subscription recovers nothing, and a stale or
 * duplicate publication must never show a wrong status).
 */

import { parseBoardRow, RUN_OUTCOMES, type BoardRow, type RunOutcome } from './loadBoard/row';

/** `disabled`: realtime switched off by config (everything polls). */
export type RealtimeState = 'disabled' | 'connecting' | 'connected' | 'disconnected';

export interface SubscribedContext {
  /** True when the SDK reconnected after a gap and tried to recover missed publications. */
  wasRecovering: boolean;
  /** True when that recovery got everything. */
  recovered: boolean;
}

export interface SubscriptionHandlers {
  onPublication(data: unknown): void;
  onSubscribed(ctx: SubscribedContext): void;
  /** The channel was refused (subscribe proxy 403) or failed; other channels are unaffected. */
  onError(): void;
}

export interface RealtimeTransport {
  connect(getToken: () => Promise<string>): void;
  disconnect(): void;
  /** Listen to the connection state; returns the unsubscribe. */
  onState(cb: (s: RealtimeState) => void): () => void;
  /** Subscribe to one channel; returns the unsubscribe. */
  subscribe(channel: string, handlers: SubscriptionHandlers): () => void;
}

/** A publication the portal understands (the relay payloads of service/src/notify.ts, camelCased). */
export type LivePublication =
  | { type: 'load.transition'; loadId: string; carrierId: string | null; from: string; to: string; at: string }
  | { type: 'load.document'; loadId: string; carrierId: string | null; kind: string; documentId: string; at: string }
  | { type: 'load.rejected'; loadId: string; carrierId: string | null; reason: string | null; at: string }
  /** 0087: the driver's app stored a point for this load. A hint only: never coordinates. */
  | { type: 'load.position'; loadId: string; carrierId: string | null; at: string }
  /** 0079 load_create (so load_create_full too): a new load on this carrier. */
  | { type: 'load.created'; loadId: string; carrierId: string | null; loadNumber: string; at: string }
  /**
   * 0079: something on the load changed that is not a status move. `kind` fields | stop | stamp | price;
   * `change` names the stop door (stop_added, arrived...); `fields` the header KEYS load_update touched
   * (never values, never amounts). `stopSeq` is the relay's `seq`: the STOP's position on the load, not an
   * event counter, so it never orders hints (an edit to stop 1 after one to stop 3 is not stale). Stale or
   * duplicate hints are harmless anyway: the screen re-reads, it never patches from the payload.
   */
  | {
      type: 'load.updated';
      loadId: string;
      carrierId: string | null;
      kind: string;
      change: string | null;
      stopSeq: number | null;
      fields: string[];
      at: string;
    }
  /** 0116 app.notify_task (r3-tasks): a task was created, closed, reopened, reassigned or edited. A hint on
   *  the user channel of the people it concerns; the screen re-reads through RLS. */
  | { type: 'task.changed'; taskId: string; at: string }
  /** 0265/0266 (activity-feed): someone in the agency did something this user may see in the Activity feed.
   *  A hint on the user channel with no kind, id, name or amount: the bell re-reads activity_feed. */
  | { type: 'activity.new'; agencyId: string; at: string }
  /** loadboard-leads (0363, design 6): a lead of the agency was marked, changed, booked, lost or expired. Ids only
   *  (`leadId` null = several, the expiry sweep). Quiet: only the Leads view and the board re-read on it. */
  | { type: 'loadboard.lead'; agencyId: string; leadId: string | null; at: string }
  /** loadboard-truck-search (0369, design 7): a location request of this user changed (sent, answered, expired).
   *  Ids and the state only, never a coordinate or a city. Quiet: only the search bar's truck origin re-reads. */
  | { type: 'truck.location'; agencyId: string; truckId: string; requestId: string | null; state: string | null; at: string }
  | LoadBoardDelivery
  | LoadBoardRun;

/**
 * loadboard-unified (design 5.1, 6.5): one chunk of a run's rows on `board:<user_id>`. Unlike every other
 * publication this one IS the data (the namespace keeps no history and nothing stores the rows): chunks are
 * numbered `seq` of `total` and the last says `complete`; `lib/loadBoard/merge.ts` assembles them.
 */
export interface LoadBoardDelivery {
  type: 'loadboard.delivery';
  runId: string;
  searchId: string | null;
  agencyId: string;
  seq: number;
  total: number;
  complete: boolean;
  rows: BoardRow[];
}

/** The run's outcome on `board:<user_id>`: every outcome, `expired` for a redelivery the cache let go.
 *  `message` is Truckstop's own words, only with `provider_refused` (shown verbatim). */
export interface LoadBoardRun {
  type: 'loadboard.run';
  runId: string;
  searchId: string | null;
  outcome: RunOutcome;
  rowCount: number;
  message: string | null;
}

/** The safety poll while the socket is up (a missed hint, a dropped socket frame). */
export const POLL_CONNECTED_MS = 120_000;
/** The fast poll while it is not (down, connecting, or realtime disabled). */
export const POLL_DISCONNECTED_MS = 20_000;

const str = (v: unknown): v is string => typeof v === 'string';
const strOrNull = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

/** A chunk whose numbering or rows do not hold is refused whole (null): the run then looks incomplete and
 *  the net asks for a redelivery. A row without a Truckstop id refuses the chunk; a bad field reads as null. */
function parseDelivery(r: Record<string, unknown>): LoadBoardDelivery | null {
  if (!str(r.run_id) || !str(r.agency_id) || !Array.isArray(r.rows)) return null;
  const { seq, total } = r;
  if (!isCount(seq) || !isCount(total) || seq < 1 || seq > total) return null;
  if (r.complete !== (seq === total)) return null;
  const rows: BoardRow[] = [];
  for (const raw of r.rows) {
    const row = parseBoardRow(raw);
    if (row === null) return null;
    rows.push(row);
  }
  return { type: 'loadboard.delivery', runId: r.run_id, searchId: strOrNull(r.search_id), agencyId: r.agency_id, seq, total, complete: seq === total, rows };
}

function parseRun(r: Record<string, unknown>): LoadBoardRun | null {
  if (!str(r.run_id) || !isCount(r.row_count)) return null;
  const outcome = RUN_OUTCOMES.find((o) => o === r.outcome);
  if (!outcome) return null;
  return { type: 'loadboard.run', runId: r.run_id, searchId: strOrNull(r.search_id), outcome, rowCount: r.row_count, message: strOrNull(r.message) };
}

/** A relay payload -> a LivePublication; anything unexpected -> null (tolerant, never throws). */
export function parsePublication(data: unknown): LivePublication | null {
  if (typeof data !== 'object' || data === null) return null;
  const r = data as Record<string, unknown>;
  if (r.type === 'task.changed') return str(r.task_id) ? { type: 'task.changed', taskId: r.task_id, at: str(r.at) ? r.at : '' } : null;
  if (r.type === 'activity.new') return str(r.agency_id) ? { type: 'activity.new', agencyId: r.agency_id, at: str(r.at) ? r.at : '' } : null;
  if (r.type === 'loadboard.lead') {
    if (!str(r.agency_id) || (r.lead_id !== undefined && r.lead_id !== null && !str(r.lead_id))) return null;
    return { type: 'loadboard.lead', agencyId: r.agency_id, leadId: strOrNull(r.lead_id), at: str(r.at) ? r.at : '' };
  }
  if (r.type === 'truck.location') {
    if (!str(r.agency_id) || !str(r.truck_id)) return null;
    return { type: 'truck.location', agencyId: r.agency_id, truckId: r.truck_id, requestId: strOrNull(r.request_id), state: strOrNull(r.state), at: str(r.at) ? r.at : '' };
  }
  if (r.type === 'loadboard.delivery') return parseDelivery(r);
  if (r.type === 'loadboard.run') return parseRun(r);
  if (!str(r.load_id)) return null;
  const base = { loadId: r.load_id, carrierId: strOrNull(r.carrier_id), at: str(r.at) ? r.at : '' };
  switch (r.type) {
    case 'load.transition':
      if (!str(r.to)) return null;
      return { type: 'load.transition', ...base, from: str(r.from) ? r.from : '', to: r.to };
    case 'load.document':
      if (!str(r.document_id)) return null;
      return { type: 'load.document', ...base, kind: str(r.kind) ? r.kind : '', documentId: r.document_id };
    case 'load.rejected':
      return { type: 'load.rejected', ...base, reason: strOrNull(r.reason) };
    case 'load.position':
      // Only the keys of a hint: coordinates in a payload are never carried (the card re-reads its row).
      return { type: 'load.position', ...base };
    case 'load.created':
      return { type: 'load.created', ...base, loadNumber: str(r.load_number) ? r.load_number : '' };
    case 'load.updated':
      if (!str(r.kind)) return null;
      return {
        type: 'load.updated',
        ...base,
        kind: r.kind,
        change: strOrNull(r.change),
        stopSeq: typeof r.seq === 'number' && Number.isFinite(r.seq) ? r.seq : null,
        fields: Array.isArray(r.fields) ? r.fields.filter(str) : [],
      };
    default:
      return null;
  }
}

/** The raw payload of a team activity hint (before parsing): the For me feed ignores it, it changes nothing there. */
export function isActivityHint(data: unknown): boolean {
  return typeof data === 'object' && data !== null && (data as Record<string, unknown>).type === 'activity.new';
}

/** The user-channel hints that change nothing in the bell's For me feed (raw payload, before parsing): the team
 *  activity hint (the Activity tab reads on it), the load board lead hint (only the Leads view does) and the
 *  truck location hint (only the search bar's truck origin does; no toast either). */
const QUIET_HINTS = new Set(['activity.new', 'loadboard.lead', 'truck.location']);
export function isQuietHint(data: unknown): boolean {
  return typeof data === 'object' && data !== null && QUIET_HINTS.has((data as Record<string, unknown>).type as string);
}

/** How long until the next safety read; null while the tab is hidden (a visible again reloads once). */
export function pollDelay(state: RealtimeState, visible: boolean): number | null {
  if (!visible) return null;
  return state === 'connected' ? POLL_CONNECTED_MS : POLL_DISCONNECTED_MS;
}

/** `https://api.host` -> `wss://api.host/connection/websocket` (Caddy routes it to Centrifugo). */
export function wsUrl(apiBaseUrl: string): string {
  const base = apiBaseUrl.replace(/\/+$/, '');
  return `${base.replace(/^http/, 'ws')}/connection/websocket`;
}
